import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { defineMessage, FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { createLookupHash } from '../../services/hash-service';
import { entitiesLoad } from '../../store/entities/entities.actions';
import { addCategoryToProducts, bulkLoadProducts } from '../../store/products/products.api';
import { ProductCategoryPropType } from '../../prop-types/product-category';
import Global from '../../store/global-selectors';
import { processBulkLoadResponse } from '../../store/products/product-bulk-load-processor';
import { emptyArray } from 'ui-components/default-props/empty';

import style from './shared-classes.module.less';

const message = defineMessage({
  description: 'orderGridOtherActions.category',
  defaultMessage: 'Categorize',
  id: 'PFdjGP',
});
const ButtonText = <FormattedMessage {...message} />;

const CategoryDropdownChild = ({ category, onClick, disabled }) => {
  const childClicked = useCallback(() => onClick(category), [category, onClick]);
  return (
    <DropdownMenuItem className={style['dropdown-menu-item']} disabled={disabled} onClick={childClicked}>
      {category.name}
    </DropdownMenuItem>
  );
};

CategoryDropdownChild.propTypes = {
  category: ProductCategoryPropType.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

const CategoryButton = ({ selectedIds = emptyArray }) => {
  const dispatch = useDispatch();
  const selectedProducts = useSelector((state) => Global.selectEntitiesProductsByIds(state, { productIds: selectedIds }));
  const categories = useSelector(Global.selectEntitiesProductCategories);
  const onCategoryClicked = useCallback(
    (category) => {
      addCategoryToProducts(selectedProducts, category.categoryId).promise.then(() => {
        bulkLoadProducts(selectedIds).then((data) => {
          dispatch(
            entitiesLoad({
              entities: {
                products: createLookupHash(processBulkLoadResponse(data || {}), 'productId'),
              },
            }),
          );
        });
      });
    },
    [selectedProducts, dispatch, selectedIds],
  );

  const sortedCategories = useMemo(() => [...categories].sort((a, b) => a.name.localeCompare(b.name)), [categories]);
  return (
    <DropdownMenu closeWhenChildrenClicked dropdownMenuClassName={style['dropdown-menu']} menuPosition='bottom-right'>
      <DropdownMenuToggler className={style['action-button']} disabled={!selectedIds.length} theme='tertiary'>
        {ButtonText}
      </DropdownMenuToggler>
      {sortedCategories.map((category) => (
        <CategoryDropdownChild key={category.categoryId} category={category} disabled={!selectedIds.length} onClick={onCategoryClicked} />
      ))}
    </DropdownMenu>
  );
};

CategoryButton.propTypes = {
  selectedIds: PropTypes.arrayOf(PropTypes.string),
};

export default CategoryButton;
